import { useEffect, useMemo, useState } from "react";
import {
  createSegment,
  getSegments,
  previewSegmentUsers,
  updateSegment,
  deleteSegment,
} from "../../hooks/segmentApi";
import {
  Button,
  Card,
  Input,
  Select,
  Table,
  Modal,
  Form,
  Tag,
  Switch,
  Space,
  Popconfirm,
  message,
  Tabs,
  Row,
  Col,
  Badge,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SaveOutlined,
  CopyOutlined,
  SyncOutlined,
  StarOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import "../css/Segments.css";
import RuleBuilder from "../component/Usercomponents/RuleBuilder";
import SegmentPreview from "../component/Usercomponents/SegmentPreview";
import SegmentStats from "../component/Usercomponents/SegmentStats";

const { Option } = Select;
const { TabPane } = Tabs;

export default function Segments() {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");

  const tenantEmail = localStorage.getItem("tenantEmail");

  useEffect(() => {
    loadSegments();
  }, []);

  const loadSegments = async () => {
    try {
      setLoading(true);
      const res = await getSegments(tenantEmail);
      setSegments(res.data.data);
    } catch (error) {
      message.error("Failed to load segments");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSegment = async (values) => {
    try {
      const payload = normalizeSegmentPayload(values);
      await createSegment(payload, tenantEmail);
      message.success("Segment created successfully");
      setModalVisible(false);
      form.resetFields();
      loadSegments();
      setActiveTab("custom"); // Switch to custom tab after creation
    } catch (error) {
      message.error("Failed to create segment");
    }
  };

  const handleUpdateSegment = async (id, values) => {
    try {
      const payload = normalizeSegmentPayload(values);
      await updateSegment(id, payload, tenantEmail);
      message.success("Segment updated successfully");
      setModalVisible(false);
      setSelectedSegment(null);
      loadSegments();
    } catch (error) {
      message.error("Failed to update segment");
    }
  };

  const handleDeleteSegment = async (id) => {
    try {
      await deleteSegment(id, tenantEmail);
      message.success("Segment deleted successfully");
      loadSegments();
    } catch (error) {
      message.error("Failed to delete segment");
    }
  };

  const handlePreviewSegment = async (id) => {
    try {
      setPreviewLoading(true);
      const res = await previewSegmentUsers(id, tenantEmail);

      if (res.data && res.data.data && Array.isArray(res.data.data.users)) {
        setPreviewData(res.data.data.users);
      } else if (Array.isArray(res.data.data)) {
        setPreviewData(res.data.data);
      } else {
        console.error("Unexpected preview data format:", res.data);
        setPreviewData([]);
        message.warning("No users found in this segment");
      }

      setPreviewModalVisible(true);
    } catch (error) {
      message.error("Failed to preview segment");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDuplicateSegment = (segment) => {
    form.setFieldsValue({
      ...segment,
      ruleGroups: getRuleGroupsFromSegment(segment),
      name: `${segment.name} (Copy)`,
    });
    setModalVisible(true);
  };

  const getRuleGroupsFromSegment = (segment) => {
    if (Array.isArray(segment?.ruleGroups) && segment.ruleGroups.length > 0) {
      return segment.ruleGroups;
    }
    const rules = Array.isArray(segment?.rules) ? segment.rules : [];
    return rules.length > 0
      ? [{ id: 1, operator: "AND", rules }]
      : [];
  };

  const normalizeSegmentPayload = (values) => {
    const ruleGroups = Array.isArray(values.ruleGroups)
      ? values.ruleGroups.filter((group) => (group.rules || []).length > 0)
      : [];
    const flattenedRules = ruleGroups.flatMap((group) => group.rules || []);
    return {
      ...values,
      ruleGroups,
      rules: flattenedRules,
    };
  };

  const refreshDefaultSegments = async () => {
    try {
      setRefreshing(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/segments/default/refresh`,
        {
          method: "POST",
          headers: {
            "x-email": tenantEmail,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        message.success(`Refreshed ${data.updatedCount} default segments`);
        loadSegments();
      }
    } catch (error) {
      message.error("Failed to refresh default segments");
    } finally {
      setRefreshing(false);
    }
  };

  // Filter segments based on active tab and search
  const getFilteredSegments = () => {
    if (!Array.isArray(segments)) return [];

    let filtered = segments.filter((segment) =>
      segment.name?.toLowerCase().includes(searchText.toLowerCase())
    );

    // Apply tab filter
    switch (activeTab) {
      case "default":
        filtered = filtered.filter((s) => s.type === "default");
        break;
      case "custom":
        filtered = filtered.filter((s) => s.type === "custom");
        break;
      case "all":
      default:
        // No type filter for "all" tab
        break;
    }

    // Apply category filter for default segments
    if (activeTab === "default" && categoryFilter !== "all") {
      filtered = filtered.filter((s) => s.defaultCategory === categoryFilter);
    }

    return filtered;
  };

  const filteredSegments = getFilteredSegments();

  // Get unique categories for filter dropdown
  const getDefaultSegmentCategories = () => {
    const defaultSegments = segments.filter((s) => s.type === "default");
    const categories = [
      ...new Set(defaultSegments.map((s) => s.defaultCategory).filter(Boolean)),
    ];
    return categories;
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div>
          <div className="cx-segments-name">
            {text}
            {record.type === "default" ? (
              <Tag
                color="gold"
                icon={<StarOutlined />}
                style={{ marginLeft: 8 }}
              >
                Default
              </Tag>
            ) : (
              <Tag
                color="purple"
                icon={<SettingOutlined />}
                style={{ marginLeft: 8 }}
              >
                Custom
              </Tag>
            )}
          </div>
          <div className="cx-segments-description">
            <div
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: "1.5",
                fontSize: "12px",
                color: "#8c8c8c",
              }}
            >
              {record.description || "No description"}
            </div>
            {record.defaultCategory && (
              <Tag
                color="blue"
                className="cx-segments-category"
                style={{
                  marginTop: 4,
                  fontSize: "11px",
                  padding: "0 6px",
                  height: "20px",
                  lineHeight: "18px",
                }}
              >
                {record.defaultCategory}
              </Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => (
        <Badge
          status={type === "default" ? "success" : "processing"}
          text={type === "default" ? "System" : "Custom"}
        />
      ),
    },
    {
      title: "Rules",
      dataIndex: "rules",
      key: "rules",
      render: (rules) => <Tag color="blue">{rules?.length || 0} rules</Tag>,
    },
    {
      title: "Users",
      key: "userCount",
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => handlePreviewSegment(record._id)}
          icon={<EyeOutlined />}
        >
          {record.userCount || 0}
        </Button>
      ),
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      render: (active) => (
        <Badge
          status={active ? "success" : "error"}
          text={active ? "Active" : "Inactive"}
        />
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        // Hide edit/delete for default segments
        if (record.type === "default") {
          return (
            <Tooltip title="Preview users">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => handlePreviewSegment(record._id)}
              />
            </Tooltip>
          );
        }

        return (
          <Space>
            <Tooltip title="Edit">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => {
                  setSelectedSegment(record);
                  form.setFieldsValue({
                    ...record,
                    ruleGroups: getRuleGroupsFromSegment(record),
                  });
                  setModalVisible(true);
                }}
              />
            </Tooltip>
            <Tooltip title="Duplicate">
              <Button
                type="text"
                icon={<CopyOutlined />}
                onClick={() => handleDuplicateSegment(record)}
              />
            </Tooltip>
            <Tooltip title="Preview">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => handlePreviewSegment(record._id)}
              />
            </Tooltip>
            <Popconfirm
              title="Delete this segment?"
              onConfirm={() => handleDeleteSegment(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Delete">
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // Get segment counts for badges
  const defaultSegmentsCount = segments.filter(
    (s) => s.type === "default"
  ).length;
  const customSegmentsCount = segments.filter(
    (s) => s.type === "custom"
  ).length;
  const activeSegmentsCount = segments.filter((s) => s.active).length;
  const inactiveSegmentsCount = segments.filter((s) => !s.active).length;

  const statsCards = useMemo(
    () => [
      {
        label: "Total Segments",
        value: segments.length,
      },
      {
        label: "Default Segments",
        value: defaultSegmentsCount,
      },
      {
        label: "Custom Segments",
        value: customSegmentsCount,
      },
      {
        label: "Active Segments",
        value: activeSegmentsCount,
      },
      {
        label: "Inactive Segments",
        value: inactiveSegmentsCount,
      },
    ],
    [
      segments.length,
      defaultSegmentsCount,
      customSegmentsCount,
      activeSegmentsCount,
      inactiveSegmentsCount,
    ]
  );

  return (
    <div className="segments-container cx-segments-page">
      <div className="cx-segments-hero">
        <div>
          <h1>Segment Management</h1>
          <p>
            Organize your audience into reusable segments for targeting and
            analysis.
          </p>
        </div>
        <div className="cx-segments-hero-actions">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setSelectedSegment(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            New Custom Segment
          </Button>
        </div>
      </div>

      <div className="cx-segments-stats">
        {statsCards.map((stat) => (
          <div key={stat.label} className="cx-segments-stat-card">
            <span>{stat.label}</span>
            <strong>{stat.value}</strong>
          </div>
        ))}
      </div>

      <Card title="Segments" className="cx-segments-card">
        <div className="cx-segments-toolbar">
          <Input.Search
            placeholder="Search segments..."
            onChange={(e) => setSearchText(e.target.value)}
            value={searchText}
            allowClear
            className="cx-segments-search"
          />
          <div className="cx-segments-toolbar-actions">
            {activeTab === "default" && (
              <>
                <Select
                  value={categoryFilter}
                  onChange={setCategoryFilter}
                  className="cx-segments-select"
                  placeholder="Filter by category"
                >
                  <Option value="all">All Categories</Option>
                  {getDefaultSegmentCategories().map((category) => (
                    <Option key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </Option>
                  ))}
                </Select>
                <Button
                  type="default"
                  icon={<SyncOutlined spin={refreshing} />}
                  onClick={refreshDefaultSegments}
                  loading={refreshing}
                >
                  Refresh Defaults
                </Button>
              </>
            )}
          </div>
        </div>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
        >
          <TabPane
            tab={
              <span>
                <UserOutlined /> All Segments
                {segments.length > 0 && (
                  <Badge
                    count={segments.length}
                    style={{ marginLeft: 8, backgroundColor: "#1890ff" }}
                  />
                )}
              </span>
            }
            key="all"
          >
            <Table
              columns={columns}
              dataSource={filteredSegments}
              rowKey="_id"
              loading={loading}
              scroll={{ x: "max-content", y: "60vh" }}
              expandable={{
                expandedRowRender: (record) => (
                  <div style={{ padding: "16px", background: "#fafafa" }}>
                    <SegmentStats segment={record} />
                  </div>
                ),
                rowExpandable: (record) =>
                  record.type === "custom" ||
                  record.rules?.length > 0 ||
                  record.ruleGroups?.length > 0,
              }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} segments`,
                size: "default",
              }}
              size="middle"
              style={{
                borderRadius: "8px",
                overflow: "hidden",
              }}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <StarOutlined /> Default Segments
                {defaultSegmentsCount > 0 && (
                  <Badge
                    count={defaultSegmentsCount}
                    style={{ marginLeft: 8, backgroundColor: "#faad14" }}
                  />
                )}
              </span>
            }
            key="default"
          >
            <div className="mb-3">
              <div className="alert alert-info">
                <StarOutlined /> <strong>System Segments</strong>
                <p className="mb-0 small">
                  These segments are automatically generated based on user
                  engagement with your campaigns. They update in real-time as
                  users interact with your messages.
                </p>
              </div>
            </div>

            <Table
              columns={columns}
              dataSource={filteredSegments}
              rowKey="_id"
              loading={loading}
              scroll={{ x: "max-content", y: "60vh" }}
              expandable={{
                expandedRowRender: (record) => (
                  <div style={{ padding: "16px", background: "#fafafa" }}>
                    <SegmentStats segment={record} />
                  </div>
                ),
                rowExpandable: (record) =>
                  record.type === "custom" ||
                  record.rules?.length > 0 ||
                  record.ruleGroups?.length > 0,
              }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} segments`,
                size: "default",
              }}
              size="middle"
              style={{
                borderRadius: "8px",
                overflow: "hidden",
              }}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <SettingOutlined /> Custom Segments
                {customSegmentsCount > 0 && (
                  <Badge
                    count={customSegmentsCount}
                    style={{ marginLeft: 8, backgroundColor: "#722ed1" }}
                  />
                )}
              </span>
            }
            key="custom"
          >
            <div className="mb-3">
              <div className="alert alert-warning">
                <SettingOutlined /> <strong>Custom Segments</strong>
                <p className="mb-0 small">
                  These are segments you've created manually. You can edit,
                  duplicate, or delete them as needed.
                </p>
              </div>
            </div>

            <Table
              columns={columns}
              dataSource={filteredSegments}
              rowKey="_id"
              loading={loading}
              scroll={{ x: "max-content", y: "60vh" }}
              expandable={{
                expandedRowRender: (record) => (
                  <div style={{ padding: "16px", background: "#fafafa" }}>
                    <SegmentStats segment={record} />
                  </div>
                ),
                rowExpandable: (record) =>
                  record.type === "custom" ||
                  record.rules?.length > 0 ||
                  record.ruleGroups?.length > 0,
              }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} segments`,
                size: "default",
              }}
              size="middle"
              style={{
                borderRadius: "8px",
                overflow: "hidden",
              }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Create/Edit Segment Modal */}
      <Modal
        title={
          selectedSegment ? "Edit Custom Segment" : "Create Custom Segment"
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedSegment(null);
        }}
        footer={null}
        width={800}
        zIndex={2000}
        style={{ top: 20 }}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) =>
            selectedSegment
              ? handleUpdateSegment(selectedSegment._id, values)
              : handleCreateSegment(values)
          }
          initialValues={{
            active: true,
            type: "custom",
          }}
          style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: "8px" }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Segment Name"
                rules={[
                  { required: true, message: "Please enter segment name" },
                  { min: 3, message: "Minimum 3 characters" },
                ]}
              >
                <Input placeholder="e.g., Premium Customers" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="active" label="Status" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ max: 500, message: "Maximum 500 characters" }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Describe this segment..."
              showCount
              maxLength={500}
            />
          </Form.Item>

          <Form.Item
            name="ruleGroups"
            label="Rules"
            rules={[
              { required: true, message: "At least one rule is required" },
            ]}
          >
            <RuleBuilder />
          </Form.Item>

          {/* Hidden field for type */}
          <Form.Item name="type" hidden>
            <Input type="hidden" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                {selectedSegment ? "Update Segment" : "Create Segment"}
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  setSelectedSegment(null);
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Preview Modal */}
      <Modal
        title="Segment Preview"
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        width={1000}
        footer={null}
        zIndex={2000}
        style={{ top: 20 }}
      >
        <SegmentPreview
          data={previewData}
          loading={previewLoading}
          onExport={() => {
            // Export functionality
          }}
        />
      </Modal>
    </div>
  );
}
